<script setup lang="ts">
import type { CatalogAddon } from '~/store/billing'
import type { CheckoutConfigurator } from '~/composables/useCheckoutConfigurator'

/**
 * The add-ons that belong to ONE step, rendered inside it.
 *
 * They used to be a single flat "Optional" card placed after every decision
 * they modify. Each one is now an answer to the question the step just asked —
 * the Windows licence under the OS, backups under the disk, addresses under the
 * network — so it reads as part of that choice rather than as a bill of
 * unfamiliar items at the end.
 *
 * It renders nothing at all when the step has no add-ons, which is what lets
 * every step host one unconditionally.
 */
const props = defineProps<{
  addons: CatalogAddon[]
  /** Live quantities, so the controls reflect what is in the order. */
  quantities: Record<string, number>
  configurator: CheckoutConfigurator
  /** Suppresses the "Optional" heading where the step is itself optional. */
  bare?: boolean
}>()

const labels = useBillingLabels()
const { t: $t } = useI18n()

const {
  isToggleAddon,
  incAddon,
  decAddon,
  toggleBoolAddon,
  rateForAddon,
  vcpuAddonTotalRate,
  addonIpCap,
  selectedOffering,
} = props.configurator

function addonName(addon: CatalogAddon): string {
  return labels.addonTypeLabel[addon.addon_type] ?? addon.name
}

/**
 * `catalog_addons.description` is the fallback, not the source: it is one
 * operator-entered string, seeded in pt-BR, so it would show Portuguese to an
 * English-speaking customer. Same reasoning as the label.
 */
function addonDescription(addon: CatalogAddon): string {
  return labels.addonTypeDescription[addon.addon_type] ?? addon.description ?? ''
}

/**
 * A dependency this add-on has that is not satisfied yet.
 *
 * Client access licences only mean anything on top of a Windows Server licence
 * — buying them without one is buying access to a licence you do not hold. The
 * catalog encodes no such relationship, so the rule lives in
 * `useCheckoutConfigurator` (and, authoritatively, on the order and resize
 * endpoints); this is where the customer is told about it, next to the control.
 */
function unmetRequirement(addon: CatalogAddon): string | null {
  if (addon.addon_type !== 'CAL_LICENSE') return null
  if (!props.configurator.needsWindowsLicenceFirst.value) return null
  return $t('billing.page.checkout.addons.requiresWindowsLicence')
}

function quantityOf(addon: CatalogAddon): number {
  return props.quantities[addon.code] ?? 0
}

function atCeiling(addon: CatalogAddon): boolean {
  const quantity = quantityOf(addon)
  const cap = addonIpCap(addon)
  return (
    // An unmet dependency stops the control here as well as in the blockers:
    // letting somebody add CALs and only telling them at the CTA wastes the
    // one decision they were making.
    !!unmetRequirement(addon) ||
    (addon.max_quantity != null && quantity >= addon.max_quantity) ||
    (cap != null && quantity >= cap)
  )
}
</script>

<template>
  <div
    v-if="addons.length > 0"
    :class="bare ? '' : 'mt-5 border-t border-default pt-4'"
  >
    <p v-if="!bare" class="mb-2 text-xs font-medium text-muted">
      {{ $t('billing.page.checkout.addons.groupTitle') }}
    </p>

    <div class="divide-y divide-default">
      <div
        v-for="addon in addons"
        :key="addon.id"
        class="flex items-center justify-between gap-3 py-3"
      >
        <div class="min-w-0 flex-1">
          <div class="text-sm font-medium">{{ addonName(addon) }}</div>

          <!-- What it actually IS, in one sentence.
               The names alone are trade vocabulary — "Snapshot slot" and "CAL"
               tell a customer nothing, and an unexplained line next to a price
               is one people skip rather than risk. -->
          <p class="mt-0.5 text-xs leading-relaxed text-toned">
            {{ addonDescription(addon) }}
          </p>

          <div class="mt-0.5 text-xs text-muted">
            <template v-if="addon.quantity_follows_vcpu">
              {{ $t('billing.page.checkout.addons.perVcpu', { rate: rateForAddon(addon) }) }}
              <template v-if="selectedOffering">
                ·
                {{
                  $t('billing.page.checkout.addons.perVcpuTotal', {
                    rate: vcpuAddonTotalRate(addon),
                    vcpu: selectedOffering.vcpu,
                  })
                }}
              </template>
            </template>
            <template v-else>{{ rateForAddon(addon) }}/h</template>
          </div>

          <!-- A dependency the customer cannot satisfy from here is not a
               warning, it is an instruction. -->
          <p
            v-if="unmetRequirement(addon)"
            class="mt-1 flex items-start gap-1.5 text-xs text-warning"
          >
            <UIcon
              name="i-lucide-circle-alert"
              class="mt-px size-3.5 shrink-0"
              aria-hidden="true"
            />
            {{ unmetRequirement(addon) }}
          </p>
        </div>

        <!-- Counted add-on. The buttons are `size="sm"` with a 44px minimum:
             these are the primary control for extra addresses and disks on a
             phone, and an `xs` icon button is roughly half the touch target a
             thumb can reliably hit. -->
        <div v-if="!isToggleAddon(addon)" class="flex items-center gap-1">
          <UButton
            size="sm"
            variant="outline"
            icon="i-lucide-minus"
            class="min-h-11 min-w-11 justify-center"
            :aria-label="$t('billing.page.checkout.addons.decrease', { name: addonName(addon) })"
            :disabled="quantityOf(addon) === 0"
            @click="decAddon(addon)"
          />
          <!-- The number is the live value of the two buttons beside it, so it
               is announced when it changes rather than only redrawn. -->
          <output
            class="w-12 text-center font-mono text-sm"
            aria-live="polite"
            aria-atomic="true"
          >
            {{ quantityOf(addon) }}
          </output>
          <UButton
            size="sm"
            variant="outline"
            icon="i-lucide-plus"
            class="min-h-11 min-w-11 justify-center"
            :aria-label="$t('billing.page.checkout.addons.increase', { name: addonName(addon) })"
            :disabled="atCeiling(addon)"
            @click="incAddon(addon)"
          />
        </div>

        <USwitch
          v-else
          :model-value="quantityOf(addon) > 0"
          :disabled="!!unmetRequirement(addon) && quantityOf(addon) === 0"
          :aria-label="addonName(addon)"
          @update:model-value="toggleBoolAddon(addon)"
        />
      </div>
    </div>
  </div>
</template>
